import SV from 'server-vars';
import { isAbTestVariant } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import { sessionStorage } from 'dibs-browser-storage';
import { getEverLoggedIn, getBuyerId } from 'dibs-cookie-jar';
import tracking from 'dibs-tracking';
import { PAGE_TYPE, BROWSE_TYPES } from '../../utils/pageConstants';
import { gaLabels } from '../../authModal/postAuth/constants';
import { VisitCountModel } from 'dibs-visit-tracking/exports/visitCountModel';
import authModalLoader from '../../utils/AuthModalLoader';
import * as postAuthLoader from './helpers/postAuthLoader';
import isPageType from '../../utils/isPageType';
import { isPlaReferrer } from './helpers/referrerHelper';
import {
    fireRegistrationTriggerABTestTracking,
    throttledScroll,
    AUTH_MODAL_SHOWN,
    REGISTRATION_TRIGGER,
    PAGE_TYPE_SB,
} from '../../helpers/autoRegistrationTriggerAbTestHelpers';

const isRegistrationTriggerVariant =
    typeof window !== 'undefined' ? isAbTestVariant(document.cookie, REGISTRATION_TRIGGER) : false;

const isAllowedPageType = () => {
    const isPdpPrintPage = SV.get('settings.isPrintPDP');
    const allowedPageTypes = [PAGE_TYPE.HOME, PAGE_TYPE.CREATOR, PAGE_TYPE.DEALER, PAGE_TYPE.PDP];

    const isAllowedPage = isPageType([...allowedPageTypes, ...BROWSE_TYPES]);

    return isAllowedPage && !isPdpPrintPage;
};

/**
 * should visitor be prompted to login?
 * @returns {boolean}
 */
const isBlocked = function isBlocked() {
    const searchParams = new URLSearchParams(location.search);
    const loggedIn = getBuyerId(document.cookie);
    if (loggedIn || searchParams.has('modal') || !isAllowedPageType() || isPlaReferrer()) {
        return true;
    }
    return false;
};

/**
 * Check if the 1st page visited was a browse page
 * @param {*} visitCountModel
 */
const isBrowsePageLanding = visitCountModel => {
    return (
        isRegistrationTriggerVariant &&
        visitCountModel.get('session') === visitCountModel.get('sbPromptLimit') &&
        isPageType(PAGE_TYPE_SB)
    );
};

/**
 * Should modal be prompted
 * @param {boolean}
 */
const shouldPromptLoginRegistration = visitCountModel => {
    // if Browse page, prepare prompt
    if (isBrowsePageLanding(visitCountModel)) {
        return true;
    }
    return visitCountModel.get('session') === visitCountModel.get('regPromptLimit');
};

const canAutoPromptUser = () => {
    const visitCountModel = new VisitCountModel();
    const isModalBlocked = isBlocked();
    const shouldOpenModal = shouldPromptLoginRegistration(visitCountModel);
    if (shouldOpenModal && !isModalBlocked) {
        return true;
    } else if (shouldOpenModal && isModalBlocked) {
        visitCountModel.increment('regPromptLimit');
        visitCountModel.save();
        return false;
    } else {
        return false;
    }
};

/**
 * prompts visitor to register if it's due
 * @return {boolean} true if login prompted
 */
const openLoginModal = () => dispatch => {
    const flow = authModalLoader.constants.PAGEVIEW_LIMIT_FLOW;
    const ga = {
        label: gaLabels.PAGEVIEW_LIMIT_REACHED,
    };

    tracking.trackEvent({
        category: 'promo interaction',
        action: 'auto modal displayed',
        label: 'auto pageview',
    });

    const visitCountModel = new VisitCountModel();

    if (!getEverLoggedIn(document.cookie)) {
        if (isBrowsePageLanding(visitCountModel)) {
            window.addEventListener('scroll', throttledScroll);
        } else if (!sessionStorage.getItem(AUTH_MODAL_SHOWN)) {
            sessionStorage.setItem(AUTH_MODAL_SHOWN, 'shown');
            fireRegistrationTriggerABTestTracking('modal_display');
            postAuthLoader.autoPromptRegistration({ flow, launchOrigin: ga.label });
        }
    } else {
        authModalLoader.show({
            action: authModalLoader.constants.LOGIN,
            flow,
            ga,
            fireEntryGa: true,
        });
        sessionStorage.setItem(AUTH_MODAL_SHOWN, 'shown');
    }
    dispatch({ type: 'noop' });
};

/**
 * Checks condition for auto prompt user.
 * Opens modal if condition is satisfied.
 * Condition:
 * page view >= 2,
 * home / browse / creator / pdp (not print) page type,
 * User is not logged in and AuthModal is not opened.
 *
 * @return {Promise}
 */
const loginPromptRule = function loginPromptRule() {
    return Promise.resolve(canAutoPromptUser());
};

export default {
    name: 'loginPrompt',
    rule: loginPromptRule,
    action: openLoginModal,
    priority: 250,
};
